import { defineStore } from 'pinia';
import API from "@/shared/helpers/API";
import {useUserStore, useRoomsStore, useUnchangingStore} from "@/console-new/stores";
import {isAndroid, isNative} from "@/shared/helpers/webapp";
import {formatDateOnly, getLocalDateFromServer} from "@/shared/helpers/dates";

// Fallback reservation length (minutes) used to size timeline blocks when the reservation has no explicit duration.
const DEFAULT_RESERVATION_DURATION = 90;

export const useRoomsOrdersStore = defineStore('roomsOrders', {
	state: () => ({
		shouldOpenFirstRoomWithNotification: false,
		loadingViewedCall: false,
		askedAssistanceFilter: false,
		lastPlayedCallSoundTime: null,
		minSecondsBetweenSounds: 30,
		roomsTablesCalls: [],
		manageReservationId: null,
		editReservationId: null,
		editReservationLoading: false,
		editReservationTables: false,
		newReservationTableId: null,
		newReservationPreset: null,
		reservations: [],
		reservationsTablesActive: false,
		reservationsViewMode: (typeof localStorage !== "undefined" && localStorage.getItem("reservations_view_mode")) === "timeline" ? "timeline" : "list",
		reservationsSearch: null,
		reservationsSelectedDate: new Date(),
		reservationsCurrDate: [],
		reservationsTimeslots: [],
		reservationsFilters: {},
		reservationsDayToPrint: null,
		standardStatus: ["confirmed", "arrived"],
		allStatuses: ["confirmed", "arrived", "noshow", "canceled", "finished"],
		waitlistSheetOpen: false,
		waitlistCurrDate: [],
		waitlistWaitingCount: 0,
		newWaitlistOpen: false,
		manageWaitlistEntry: null
	}),
	getters: {
		roomsStore: state => useRoomsStore(),
		roomsTablesCallsNotViewed: state => {
			if(!state.roomsTablesCalls) return [];
			return state.roomsTablesCalls.filter(call => !call.viewed);
		},
		rtcIds: state => {
			let finalIds = {};
			if(!state.roomsTablesCallsNotViewed) return finalIds;
			for(let i = 0; i < state.roomsTablesCallsNotViewed.length; i++) {
				finalIds[state.roomsTablesCallsNotViewed[i].room_table_call_id] = state.roomsTablesCallsNotViewed[i];
			}
			return finalIds;
		},
		rtcTablesIds: state => {
			let finalIds = {};
			if(!state.roomsTablesCallsNotViewed) return finalIds;
			for(let i = 0; i < state.roomsTablesCallsNotViewed.length; i++) {
				let currentItem = state.roomsTablesCallsNotViewed[i];
				if(!finalIds[currentItem.room_table_id]) {
					finalIds[currentItem.room_table_id] = {};
				}
				finalIds[currentItem.room_table_id][currentItem.room_table_call_id] = currentItem;
			}
			return finalIds;
		},
		rtcRoomsIds: state => {
			let finalIds = {};
			if(!state.roomsTablesCallsNotViewed) return finalIds;
			for(let i = 0; i < state.roomsTablesCallsNotViewed.length; i++) {
				let currentItem = state.roomsTablesCallsNotViewed[i];
				if(!finalIds[currentItem.room_id]) {
					finalIds[currentItem.room_id] = {};
				}
				finalIds[currentItem.room_id][currentItem.room_table_call_id] = currentItem;
			}
			return finalIds;
		},
		hasReservations: state => {
			return state.currBusiness && (state.currBusiness.active_plan === "allinone" || state.currBusiness.active_plan === "enterprise");
		}
	},
	actions: {
		getDayReservations(callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) {
				// Call callback with empty data to clear loading state
				callback && callback([]);
				return;
			}

			const date = this.reservationsSelectedDate ? formatDateOnly(this.reservationsSelectedDate) : null;
			let status = date && date < formatDateOnly(new Date()) ? ["confirmed", "arrived", "noshow", "canceled", "finished"] : ["confirmed", "arrived"];

			if(this.reservationsFilters && Object.keys(this.reservationsFilters).length) {
				if(this.reservationsFilters.status && this.reservationsFilters.status.length) {
					status = this.reservationsFilters.status;
				}
			}
			let experiences = null;
			if(this.reservationsFilters && this.reservationsFilters.experiences && this.reservationsFilters.experiences.length) {
				experiences = this.reservationsFilters.experiences;
			}
			let rooms = null;
			if(this.reservationsFilters && this.reservationsFilters.rooms && this.reservationsFilters.rooms.length) {
				rooms = this.reservationsFilters.rooms;
			}

			API.init().getDayReservations(userStore.currBusinessId, date, status, experiences, rooms, this.reservationsSearch, response => {
				if (response && response.result === "OK") {
					this.reservationsCurrDate = this.normalizeReservations(response.reservations);
					this.reservationsTimeslots = response.timeslots || [];
					callback && callback(this.reservations);
				} else {
					this.reservationsCurrDate = [];
					this.reservationsTimeslots = [];
					callback && callback([]);
				}
			});
		},
		getDayWaitlist(callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) {
				callback && callback([]);
				return;
			}

			const date = this.reservationsSelectedDate ? formatDateOnly(this.reservationsSelectedDate) : formatDateOnly(new Date());
			API.init().getDayWaitlist(userStore.currBusinessId, date, response => {
				if (response && response.result === "OK") {
					this.waitlistCurrDate = this.normalizeWaitlist(response.waitlist);
					this.waitlistWaitingCount = Number(response.waiting_count) || 0;
					callback && callback(this.waitlistCurrDate);
				} else {
					this.waitlistCurrDate = [];
					this.waitlistWaitingCount = 0;
					callback && callback([]);
				}
			});
		},
		normalizeWaitlist(entries) {

			entries.forEach(entry => {
				const serverDate = entry.date;
				entry.fullDate = getLocalDateFromServer(serverDate+" "+entry.time);
				entry.date = formatDateOnly(entry.fullDate);
				entry.timeDisplay = entry.fullDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				entry.tolerance_minutes = Number(entry.tolerance_minutes) || 0;
				entry.people = Number(entry.people);
				entry.children = entry.children !== null ? Number(entry.children) : null;
				entry.dogs = entry.dogs !== null ? Number(entry.dogs) : null;
				entry.notes = entry.notes ? entry.notes : null;
				if(entry.tolerance_minutes > 0) {
					const windowStart = new Date(entry.fullDate.getTime() - entry.tolerance_minutes * 60 * 1000);
					const windowEnd = new Date(entry.fullDate.getTime() + entry.tolerance_minutes * 60 * 1000);
					entry.toleranceWindow = windowStart.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})+" - "+windowEnd.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				} else {
					entry.toleranceWindow = null;
				}
				entry.fitsTimeFull = entry.fits_time ? getLocalDateFromServer(serverDate+" "+entry.fits_time) : null;
				entry.fitsTimeDisplay = entry.fitsTimeFull ? entry.fitsTimeFull.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : null;
				entry.loadingUpdate = false;
			});
			return entries;
		},
		updateWaitlistStatus(waitlistId, status, callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) return;

			API.init().updateWaitlistStatus(userStore.currBusinessId, waitlistId, status, response => {
				if (response && response.result === "OK") {
					callback && callback(response);
				} else {
					callback && callback();
				}
			});
		},
		setReservationsViewMode(mode) {
			this.reservationsViewMode = mode === "timeline" ? "timeline" : "list";
			if(typeof localStorage !== "undefined") {
				localStorage.setItem("reservations_view_mode", this.reservationsViewMode);
			}
		},
		updateReservationStatus(reservationId, status, refund, callback = null) {
			const userStore = useUserStore();
			if(userStore.currBusinessId === null) return;

			API.init().updateReservationStatus(userStore.currBusinessId, reservationId, status, refund, response => {
				if (response && response.result === "OK") {
					callback && callback(response);
				} else {
					callback && callback();
				}
			});
		},
		normalizeReservations(reservations) {

			reservations.forEach(reservation => {
				reservation.fullDate = getLocalDateFromServer(reservation.date+" "+reservation.time);
				reservation.date = formatDateOnly(reservation.fullDate);
				reservation.time = reservation.fullDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				reservation.timeDisplay = reservation.fullDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
				reservation.timeUntil = null;
				reservation.dateDisplay = reservation.fullDate.toLocaleDateString([], {month: 'short', day: '2-digit'});
				reservation.children = Number(reservation.children);
				reservation.dogs = Number(reservation.dogs);
				reservation.duration = reservation.duration ? Number(reservation.duration) : DEFAULT_RESERVATION_DURATION;
				reservation.endDate = new Date(reservation.fullDate.getTime() + reservation.duration * 60 * 1000);
				reservation.amount_charged = Number(reservation.amount_charged);
				reservation.notes = reservation.notes ? reservation.notes : null;
				reservation.loadingUpdate = false;

				reservation.tablesRoom = this.getTablesRoomFromIds(reservation.tables);

				reservation.allergensList = [];
				reservation.allergens.forEach(allergen => {
					const unchangingStore = useUnchangingStore();
					if(unchangingStore.allergens.hasOwnProperty(allergen)) {
						reservation.allergensList.push(unchangingStore.allergens[allergen]);
					}
				});
			});
			return reservations;
		},
		getTablesRoomFromIds(roomTableIds) {
			let result = [];
			roomTableIds.forEach(roomTableId => {

				this.roomsStore.roomsTables.forEach(room => {
					room.tables.forEach(table => {
						if(table.room_table_id === Number(roomTableId)) {
							result.push({
								table_room_id: table.room_table_id,
								room_name: room.name,
								table_name: table.table_name
							});
						}
					});
				});

			});
			return result;
		},
		getReservationById(reservationId) {
			let reservation = this.reservations.find(reservation => reservation.reservation_id === reservationId);
			if(!reservation) {
				reservation = this.reservationsCurrDate.find(reservation => reservation.reservation_id === reservationId);
			}
			return reservation;
		},
		playNewRoomTableCallSound(roomId) {
			if(this.lastPlayedCallSoundTime) {
				if(new Date().getTime() - this.lastPlayedCallSoundTime < this.minSecondsBetweenSounds * 1000) {
					return;
				}
			}
			if(!isAndroid()) {
				const foundRoom = this.roomsStore.roomsIds[roomId];
				if(!foundRoom || !foundRoom.notification_sound) {
					return;
				}
				this.lastPlayedCallSoundTime = new Date().getTime();
				if(foundRoom.audioNewOrder) {
					foundRoom.audioNewOrder.play().catch(() => {
						console.warn("Audio play not allowed");
					});
				} else {
					foundRoom.audioNewOrder = (new Audio('https://img.qromo.io/audio/'+foundRoom.notification_sound+'.mp3'));
					foundRoom.audioNewOrder.play().catch(() => {
						console.warn("Audio play not allowed");
					});
				}
			}
		},
		checkNewCallsToPlaySound(receivedCalls) {
			if(this.lastPlayedCallSoundTime) {
				if(new Date().getTime() - this.lastPlayedCallSoundTime < this.minSecondsBetweenSounds * 1000) {
					return;
				}
			}
			let newCalls = receivedCalls || [];
			if(newCalls && newCalls.length) {
				for(let i = 0; i < newCalls.length; i++) {
					let roomTableCall = newCalls[i];
					if(roomTableCall.room_table_call_id && !roomTableCall.viewed && !this.rtcIds[roomTableCall.room_table_call_id]) {
						this.playNewRoomTableCallSound(roomTableCall.room_id);
						return;
					}

				}
			}
		},
		getFirstRoomIdWithNotification() {
			if(!this.roomsStore.rooms?.length || !this.roomsTablesCalls?.length) { return null; }
			return this.roomsTablesCalls.find(call => call.room_id && this.roomsStore.roomsIds[call.room_id])?.room_id;
		},
		viewedRoomTableCall(roomTableId, callback = null) {
			if(this.loadingViewedCall) { return; }
			this.loadingViewedCall = true;
			API.init().viewedRoomTableCall(useUserStore().currBusinessId, roomTableId, (r) => {
				let successUpdate = r && r.result && r.result === "OK";
				if(successUpdate) {
					this.roomsTablesCalls = this.roomsTablesCalls.filter(call => call.room_table_id !== roomTableId);
				}
				this.loadingViewedCall = false;
				callback && callback(successUpdate);
			});
		},
		enabledTableCalls() {
			return this.roomsStore.rooms?.length && this.roomsStore.rooms?.find && !!this.roomsStore.rooms.find(room => room.enabledCalls && room.acceptOrders);
		},
		resetRoomsTablesCalls() {
			this.roomsTablesCalls = [];
			this.askedAssistanceFilter = false;
		},
		normalizeRoomsTablesCalls(calls) {
			return calls.map(call => {
				call.room_table_call_id = Number(call.room_table_call_id);
				call.room_table_id = Number(call.room_table_id);
				call.room_id = Number(call.room_id);
				call.viewed = call.hasOwnProperty('viewed') ? call.viewed : false;
				return call;
			})
		},
		initRoomsTablesCalls(calls) {
			let newCalls = this.normalizeRoomsTablesCalls(calls || []);
			this.checkNewCallsToPlaySound(newCalls);
			this.roomsTablesCalls = newCalls;
		},
	}
});